// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Illustration,
  Input,
  Input2FA,
  toast,
} from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import {
  useApprovalWorkflowActionPreRequest,
  useApprovalWorkflowChallengeRequest,
} from 'hooks/mutations/useApprovalDecisionMutation'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ApprovalDecision, ApprovalStatuses, ApprovalWorkflowActionResponse } from 'types/approvals'
import { BaseErrorResponse } from 'types/beneficiaries'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { logSentryError } from 'utils/sentry'
import track from 'utils/tracker'
import { useApprovalDecisionCopyTranslation } from './hooks/useApprovalDecisionCopyTranslation'
import { groupPaymentsTotalByCurrency } from './hooks/useTotalValueOfPayments'

interface ApprovalDecisionModalProps {
  decision: ApprovalDecision
  selectedItems?: unknown[]
}

const ApprovalDecisionModal = ({ decision, selectedItems = [] }: ApprovalDecisionModalProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { currentAccount } = useAccountsContext()
  const queryClient = useQueryClient()
  const {
    approvalsConfig: { handleApprovalDecisionModal, handleTableRowCheck },
  } = useApprovalsContext()

  const [approvalDecisionFormState, setApprovalDecisionFormState] = useState({
    hasReviewedPayment: false,
    rejectionReason: '',
    challengeCode: '',
    twoFactorCode: '',
    twoFactorCodeError: false,
    staleSelectedItems: selectedItems,
  })

  const { mutate: approvalWorkflowActionPreRequest, isPending: isLoadingPreRequest } =
    useApprovalWorkflowActionPreRequest()

  const {
    mutate: approvalWorkflowChallengeRequest,
    isPending: isLoadingPostRequest,
    isSuccess: isSuccessPostRequest,
  } = useApprovalWorkflowChallengeRequest()

  const onCompleteHandleReviewPayment = (
    error: ApprovalWorkflowActionResponse,
    isRejectDecisionCheckboxClick?: boolean
  ) => {
    // pre condition to get challengeCode value
    if (error?.status === 428) {
      if (decision === Approval.DECISION.REJECT) {
        if (approvalDecisionFormState?.hasReviewedPayment) {
          // toggle hasReviewedPayment value of checkbox for reject if previously checked
          // albeit always store latest challengeCode for 2FA
          isRejectDecisionCheckboxClick
            ? setApprovalDecisionFormState({
                ...approvalDecisionFormState,
                hasReviewedPayment: !approvalDecisionFormState?.hasReviewedPayment,
                challengeCode: error?.data?.challenge?.value,
              })
            : setApprovalDecisionFormState({
                ...approvalDecisionFormState,
                challengeCode: error?.data?.challenge?.value,
              })
          return
        }

        // initial toggle for hasReviewedPayment value of checkbox for reject when checkbox is clicked
        // albeit always store latest challengeCode for 2FA
        isRejectDecisionCheckboxClick
          ? setApprovalDecisionFormState({
              ...approvalDecisionFormState,
              hasReviewedPayment: !approvalDecisionFormState?.hasReviewedPayment,
              challengeCode: error?.data?.challenge?.value,
            })
          : setApprovalDecisionFormState({
              ...approvalDecisionFormState,
              challengeCode: error?.data?.challenge?.value,
            })
      } else {
        // toggle for decision approve only
        setApprovalDecisionFormState({
          ...approvalDecisionFormState,
          hasReviewedPayment: !approvalDecisionFormState?.hasReviewedPayment,
          challengeCode: error?.data?.challenge?.value,
        })
      }
    } else {
      toast({ header: t('oopsSomethingWentWrongContracted'), variant: 'error' })
      logSentryError('Error from ApprovalDecisionModal - handleReviewPayment', error)
    }
  }

  const handleReviewPayment = (isRejectDecisionCheckboxClick?: boolean) => {
    const selectedTransactionReferences = selectedItems.map((item: { correlationKey?: string }) => {
      return item?.correlationKey
    })

    approvalWorkflowActionPreRequest(
      {
        action: decision,
        reference: selectedTransactionReferences,
        ...(decision === Approval.DECISION.REJECT && {
          rejectionReason: approvalDecisionFormState?.rejectionReason,
        }),
      },
      {
        onError: (error: ApprovalWorkflowActionResponse) =>
          onCompleteHandleReviewPayment(error, isRejectDecisionCheckboxClick),
      }
    )
  }

  const onApprovalDecisionSuccess = () => {
    const currentAccountReference = currentAccount?.reference || ''
    const totalValueOfPayments = groupPaymentsTotalByCurrency(selectedItems)
    queryClient.invalidateQueries({
      queryKey: reactQueryKeys.approvalsTransactions(
        currentAccountReference,
        ApprovalStatuses.PENDING
      ),
    })
    handleTableRowCheck([])
    const titleOfEventInput =
      decision === Approval.DECISION.APPROVE
        ? AmplitudeEvent?.EOF_APPROVE_TRANSACTION_SUCCESS
        : AmplitudeEvent?.EOF_REJECT_TRANSACTION_SUCCESS
    track.Amp.track(titleOfEventInput, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      numberOfSelectedItems: selectedItems?.length,
      totalValueOfPayments,
    })
  }

  const onApprovalDescisionError = (error: BaseErrorResponse) => {
    if (error?.status === 403 && !error?.data?.code) {
      setApprovalDecisionFormState({ ...approvalDecisionFormState, twoFactorCodeError: true })
    } else {
      toast({ header: t('oopsSomethingWentWrongContracted'), variant: 'error' })
      logSentryError('Error from ApprovalDecisionModal - handleApprovalDecisionChallenge', error)
      const titleOfEventInput =
        decision === Approval.DECISION.APPROVE
          ? AmplitudeEvent?.EOF_APPROVE_TRANSACTION_ERROR
          : AmplitudeEvent?.EOF_REJECT_TRANSACTION_ERROR
      track.Amp.track(titleOfEventInput, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
  }

  const handleApprovalDecisionChallenge = () => {
    approvalWorkflowChallengeRequest(
      {
        challenge: approvalDecisionFormState?.challengeCode,
        tokenResponse: approvalDecisionFormState?.twoFactorCode,
      },
      {
        onSuccess: () => onApprovalDecisionSuccess(),
        onError: (error: BaseErrorResponse) => {
          onApprovalDescisionError(error)
        },
      }
    )
  }

  const selectedItemsCount = selectedItems?.length

  useEffect(() => {
    if (approvalDecisionFormState?.twoFactorCode?.length === TWO_FA_REQUIRED_LENGTH) {
      handleApprovalDecisionChallenge()
    }
  }, [approvalDecisionFormState.twoFactorCode])

  const {
    decisionHeadline = '',
    decisionDescription = '',
    decisionAction = '',
  } = useApprovalDecisionCopyTranslation(decision, approvalDecisionFormState?.staleSelectedItems, t)

  const onGoToTab = () => {
    const currentAccountReference = currentAccount?.reference || ''
    handleApprovalDecisionModal({ isVisible: false, isCompleted: true })
    handleTableRowCheck([])
    queryClient.invalidateQueries({
      queryKey: reactQueryKeys.approvalsTransactions(
        currentAccountReference,
        ApprovalStatuses.PENDING
      ),
    })
    void navigate(`${PathNames.APPROVALS}?tab=${decisionAction.toUpperCase()}`, {
      replace: true,
    })
  }

  const onDone = () => {
    handleApprovalDecisionModal({ isVisible: false, isCompleted: true })
    handleTableRowCheck([])
    void navigate(`${PathNames.APPROVALS}?tab=${ApprovalStatuses.PENDING}`, {
      replace: true,
    })
  }

  if (isSuccessPostRequest) {
    return (
      <Dialog
        open
        onClose={onDone}
        title={decisionHeadline}
        Illustration={
          decision === Approval.DECISION.APPROVE ? (
            <Illustration name="SealCheck" />
          ) : (
            <Illustration name="ThumbsDown" />
          )
        }
        ActionButtons={[
          <Button key="done" onClick={onDone}>
            {t('done')}
          </Button>,
          <Button key="goToTab" onClick={onGoToTab} variant="secondary">
            {t('approvals.goToTab', { tabName: decisionAction })}
          </Button>,
        ]}
      >
        <p className="text-center text-foreground-prominent text-body-regular">
          {decisionDescription}
        </p>
      </Dialog>
    )
  }

  return (
    <Dialog
      onClose={() => handleApprovalDecisionModal({ isVisible: false, isCompleted: true })}
      open
      Illustration={
        <Illustration name={decision === Approval.DECISION.APPROVE ? 'Stamp' : 'Prohibit'} />
      }
      title={`${t('approvals.decisionQuestionIntro')} ${decision?.toLowerCase()} 
      ${
        selectedItemsCount > 1
          ? t('approvals.thesePayments', { paymentCount: selectedItemsCount })
          : t('approvals.thisPayment')
      }`}
    >
      <>
        <Box flexCol centerChildren paddingY={12}>
          {isLoadingPreRequest &&
          !approvalDecisionFormState?.rejectionReason &&
          !approvalDecisionFormState?.hasReviewedPayment ? (
            <Button variant="ghost" loading={true} noPadding>
              {t('approvals.reviewNote', {
                paymentPronoun: selectedItemsCount > 1 ? t('approvals.each') : t('approvals.this'),
              })}
            </Button>
          ) : (
            <Checkbox
              id="approval-decision-review-checkbox"
              name="approval-decision-review-checkbox"
              testId="approval-decision-review-checkbox"
              label={t('approvals.reviewNote', {
                paymentPronoun: selectedItemsCount > 1 ? t('approvals.each') : t('approvals.this'),
              })}
              checked={approvalDecisionFormState?.hasReviewedPayment}
              onCheckedChange={() => handleReviewPayment(decision === Approval.DECISION.REJECT)}
            />
          )}
        </Box>
        {decision === Approval.DECISION.REJECT && (
          <Box paddingY={4}>
            <Input
              testId="approval-decision-rejection-reason-input"
              label={t('approvals.reasonForRejection')}
              placeholder={t('approvals.reason')}
              optionalText={t('optionalLabel')}
              value={approvalDecisionFormState?.rejectionReason}
              onChange={e => {
                setApprovalDecisionFormState({
                  ...approvalDecisionFormState,
                  rejectionReason: e.target.value,
                })
              }}
              onBlur={() => handleReviewPayment()}
            />
          </Box>
        )}
        <Box flexCol align="center" gapY={16}>
          <Box width="full">
            <Input2FA
              label={t('authCodeConfirm')}
              loading={isLoadingPostRequest}
              testId="approval-decision-2FA-input"
              disabled={!approvalDecisionFormState?.hasReviewedPayment}
              onChange={value =>
                setApprovalDecisionFormState({
                  ...approvalDecisionFormState,
                  twoFactorCode: value,
                })
              }
              error={!!approvalDecisionFormState?.twoFactorCodeError && t('incorrectOtpCode')}
            />
          </Box>
        </Box>
      </>
    </Dialog>
  )
}

export default ApprovalDecisionModal
